/**
 * Servicio de Gestión de Roles y Asignaciones
 * Maneja la asignación de roles a usuarios y verificación de permisos
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { RoleAssignment, SystemRole, ScopeType } from "@/types";
import { ensurePersonAndMemberExist } from "./persons";
import { normalizeName } from "@/lib/utils";

const COLLECTION = "roleAssignments";

// ==================== QUERIES ====================

/**
 * Obtiene todas las asignaciones de rol de un usuario
 */
export async function getRolesByUserId(
  userId: string,
): Promise<RoleAssignment[]> {
  // Query simple sin orderBy para evitar necesidad de índice compuesto
  const q = query(
    collection(db, COLLECTION),
    where("userId", "==", userId),
    where("status", "==", "active"),
  );
  const snapshot = await getDocs(q);
  const roles = snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() }) as RoleAssignment,
  );
  // Ordenar en memoria por fecha
  return roles.sort((a, b) => {
    const dateA = a.assignedAt?.toDate?.() || new Date(0);
    const dateB = b.assignedAt?.toDate?.() || new Date(0);
    return dateB.getTime() - dateA.getTime();
  });
}

/**
 * Obtiene el rol principal de un usuario (el de mayor jerarquía)
 */
export async function getPrimaryRole(
  userId: string,
): Promise<RoleAssignment | null> {
  const roles = await getRolesByUserId(userId);
  if (roles.length === 0) return null;

  // Ordenar por nivel de rol (1 = mayor jerarquía)
  const roleLevels: Record<SystemRole, number> = {
    admin_union: 1,
    admin_association: 2,
    club_director: 3,
    member: 4,
  };

  roles.sort((a, b) => roleLevels[a.role] - roleLevels[b.role]);
  return roles[0];
}

/**
 * Obtiene todas las asignaciones de rol para un scope específico
 */
export async function getRolesByScope(
  scopeType: ScopeType,
  scopeId: string,
): Promise<RoleAssignment[]> {
  const q = query(
    collection(db, COLLECTION),
    where("scopeType", "==", scopeType),
    where("scopeId", "==", scopeId),
    where("status", "==", "active"),
    orderBy("assignedAt", "desc"),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() }) as RoleAssignment,
  );
}

/**
 * Obtiene el director GENERAL de un club.
 *
 * Un club puede tener (a) un director general (RoleAssignment sin `section`)
 * y (b) directores de sección (con `section` definido). Esta función devuelve
 * el director general; si no existe, devuelve el primer director de sección
 * disponible (compat con clubes históricos donde no se distinguía).
 *
 * Antes: tomaba `snapshot.docs[0]` sin orden → con > 1 director activo
 * devolvía uno aleatorio. Ahora prefiere el general (sin `section`) y, si
 * hay empate, el más reciente por `assignedAt`.
 *
 * Para listar TODOS los directores (general + secciones) usar
 * `getDirectorsByClub`.
 */
export async function getClubDirector(
  clubId: string,
): Promise<RoleAssignment | null> {
  const q = query(
    collection(db, COLLECTION),
    where("scopeType", "==", "club"),
    where("scopeId", "==", clubId),
    where("role", "==", "club_director"),
    where("status", "==", "active"),
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const all = snapshot.docs.map(
    (d) => ({ id: d.id, ...d.data() }) as RoleAssignment,
  );

  // Preferir el director general (sin section), luego ordenar por fecha desc.
  const sorted = all.sort((a, b) => {
    const aGeneral = a.section ? 1 : 0;
    const bGeneral = b.section ? 1 : 0;
    if (aGeneral !== bGeneral) return aGeneral - bGeneral; // 0 (general) primero
    const aMs = a.assignedAt?.toDate?.().getTime() ?? 0;
    const bMs = b.assignedAt?.toDate?.().getTime() ?? 0;
    return bMs - aMs;
  });

  return sorted[0];
}

/**
 * Obtiene todos los directores activos de un club, agrupados por tipo.
 *
 * Devuelve:
 *   - `general`: el director general (sin `section`), si existe.
 *   - `sections`: mapa { ClubType -> RoleAssignment } con los directores de
 *     cada sección activa.
 *
 * Si hay > 1 candidato general (caso anómalo) se devuelve el más reciente.
 * Si hay > 1 candidato para una misma sección, idem.
 */
export async function getDirectorsByClub(clubId: string): Promise<{
  general?: RoleAssignment;
  sections: Partial<Record<import("@/types").ClubType, RoleAssignment>>;
}> {
  const q = query(
    collection(db, COLLECTION),
    where("scopeType", "==", "club"),
    where("scopeId", "==", clubId),
    where("role", "==", "club_director"),
    where("status", "==", "active"),
  );
  const snapshot = await getDocs(q);
  const all = snapshot.docs.map(
    (d) => ({ id: d.id, ...d.data() }) as RoleAssignment,
  );

  const byTime = (a: RoleAssignment, b: RoleAssignment) =>
    (b.assignedAt?.toDate?.().getTime() ?? 0) -
    (a.assignedAt?.toDate?.().getTime() ?? 0);

  const generals = all.filter((r) => !r.section).sort(byTime);
  const sectionMap: Partial<
    Record<import("@/types").ClubType, RoleAssignment>
  > = {};
  for (const ra of all.filter((r) => !!r.section).sort(byTime)) {
    const sec = ra.section!;
    if (!sectionMap[sec]) sectionMap[sec] = ra; // primero (más reciente) gana
  }

  return {
    general: generals[0],
    sections: sectionMap,
  };
}

/**
 * Obtiene todos los directores de una asociación
 */
export async function getDirectorsByAssociation(
  associationId: string,
): Promise<RoleAssignment[]> {
  // Primero obtener los clubes de la asociación
  const clubsQuery = query(
    collection(db, "clubs"),
    where("associationId", "==", associationId),
  );
  const clubsSnapshot = await getDocs(clubsQuery);
  const clubIds = clubsSnapshot.docs.map((doc) => doc.id);

  if (clubIds.length === 0) return [];

  // Obtener directores de esos clubes
  const roles: RoleAssignment[] = [];
  for (const clubId of clubIds) {
    const director = await getClubDirector(clubId);
    if (director) roles.push(director);
  }

  return roles;
}

/**
 * Verifica si un usuario tiene un rol específico
 */
export async function hasRole(
  userId: string,
  role: SystemRole,
  scopeId?: string,
): Promise<boolean> {
  const conditions = [
    where("userId", "==", userId),
    where("role", "==", role),
    where("status", "==", "active"),
  ];

  if (scopeId) {
    conditions.push(where("scopeId", "==", scopeId));
  }

  const q = query(collection(db, COLLECTION), ...conditions);
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Verifica si un usuario es administrador (union o association)
 */
export async function isAdmin(userId: string): Promise<boolean> {
  const roles = await getRolesByUserId(userId);
  return roles.some(
    (r) => r.role === "admin_union" || r.role === "admin_association",
  );
}

/**
 * Verifica si un usuario es director de un club específico
 */
export async function isClubDirector(
  userId: string,
  clubId: string,
): Promise<boolean> {
  return hasRole(userId, "club_director", clubId);
}

// ==================== MUTATIONS ====================

/**
 * Asigna un rol a un usuario
 *
 * Si el rol es club_director, automáticamente:
 * 1. Crea Person si no existe
 * 2. Crea Member con role=director si no existe
 */
export async function assignRole(
  data: {
    userId: string;
    userEmail: string;
    userName: string;
    role: SystemRole;
    scopeType: ScopeType;
    scopeId: string;
    scopeName: string;
    notes?: string;
    // A3+A4: Datos personales opcionales para reenviar a ensurePersonAndMemberExist
    personalData?: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      birthDate?: Date;
      idDocumentType?: string;
      idDocumentNumber?: string;
      gender?: string;
      bloodType?: string;
      address?: string;
      memberType?: string;
      emergencyContact?: { name: string; phone: string; relationship: string };
    };
  },
  assignedBy: string,
): Promise<RoleAssignment> {
  // Verificar si ya tiene este rol en este scope
  const existing = await hasRole(data.userId, data.role, data.scopeId);
  if (existing) {
    throw new Error("El usuario ya tiene este rol asignado");
  }

  // Normalizar nombre para evitar inconsistencias de capitalización
  const normalizedUserName = data.userName
    ? normalizeName(data.userName)
    : data.userName;
  data = { ...data, userName: normalizedUserName };

  // Si es director de club, verificar que no haya otro director
  if (data.role === "club_director") {
    const currentDirector = await getClubDirector(data.scopeId);
    if (currentDirector && currentDirector.userId !== data.userId) {
      throw new Error("Este club ya tiene un director asignado");
    }

    // Crear Person + Member automáticamente para el director
    await ensurePersonAndMemberExist({
      userId: data.userId,
      email: data.userEmail,
      fullName: data.userName,
      clubId: data.scopeId,
      role: "director",
      createdBy: assignedBy,
      // A3+A4: Reenviar datos personales si disponibles
      ...data.personalData,
    });
  }

  const roleData = {
    ...data,
    status: "active" as const,
    assignedBy,
    assignedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    createdBy: assignedBy,
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, COLLECTION), roleData);

  // Si es director, actualizar el club
  if (data.role === "club_director") {
    await updateDoc(doc(db, "clubs", data.scopeId), {
      directorId: data.userId,
      directorName: data.userName,
      updatedAt: serverTimestamp(),
    });
  }

  return { id: docRef.id, ...roleData } as RoleAssignment;
}

/**
 * Revoca un rol de un usuario
 */
export async function revokeRole(
  roleAssignmentId: string,
  revokedBy: string,
): Promise<void> {
  const roleRef = doc(db, COLLECTION, roleAssignmentId);
  const roleDoc = await getDoc(roleRef);

  if (!roleDoc.exists()) {
    throw new Error("Asignación de rol no encontrada");
  }

  const roleData = roleDoc.data() as RoleAssignment;

  await updateDoc(roleRef, {
    status: "inactive",
    updatedAt: serverTimestamp(),
    updatedBy: revokedBy,
  });

  // Si era director, limpiar el club
  if (roleData.role === "club_director") {
    await updateDoc(doc(db, "clubs", roleData.scopeId), {
      directorId: null,
      directorName: null,
      updatedAt: serverTimestamp(),
    });
  }
}

/**
 * Transfiere el rol de director a otro usuario
 */
export async function transferDirectorship(
  clubId: string,
  newUserId: string,
  newUserEmail: string,
  newUserName: string,
  clubName: string,
  transferredBy: string,
): Promise<RoleAssignment> {
  // Revocar director actual si existe
  const currentDirector = await getClubDirector(clubId);
  if (currentDirector) {
    await revokeRole(currentDirector.id, transferredBy);
  }

  // Asignar nuevo director
  return assignRole(
    {
      userId: newUserId,
      userEmail: newUserEmail,
      userName: newUserName,
      role: "club_director",
      scopeType: "club",
      scopeId: clubId,
      scopeName: clubName,
      notes: currentDirector
        ? `Transferido de ${currentDirector.userName}`
        : "Asignación inicial",
    },
    transferredBy,
  );
}

export const roleService = {
  getAll: async (): Promise<RoleAssignment[]> => {
    const q = query(collection(db, COLLECTION), orderBy("assignedAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as RoleAssignment,
    );
  },
  getByUserId: getRolesByUserId,
  getByScope: getRolesByScope,
  getRolesByUserId,
  getPrimaryRole,
  getRolesByScope,
  getClubDirector,
  getDirectorsByClub,
  getDirectorsByAssociation,
  hasRole,
  isAdmin,
  isClubDirector,
  assignRole: async (params: {
    userId: string;
    role: SystemRole;
    scopeType: ScopeType;
    scopeId: string;
    scopeName: string;
    assignedBy: string;
  }) => {
    // Obtener datos del usuario
    const userDoc = await getDoc(doc(db, "users", params.userId));
    if (!userDoc.exists()) {
      throw new Error("Usuario no encontrado");
    }
    const userData = userDoc.data();

    return assignRole(
      {
        userId: params.userId,
        userEmail: userData.email || "",
        userName: userData.displayName || "",
        role: params.role,
        scopeType: params.scopeType,
        scopeId: params.scopeId,
        scopeName: params.scopeName,
      },
      params.assignedBy,
    );
  },
  revokeRole,
  transferDirectorship,
};
